import { RecursivePartial } from '../types';

/**
 * Creates a factory function for generating test objects with sensible defaults.
 *
 * The factory pattern reduces boilerplate in tests by providing complete objects
 * with default values, allowing tests to only specify the properties relevant
 * to what they're testing.
 *
 * Uses shallow merging - if you need to override nested objects, create separate
 * factories for them and compose them together.
 *
 * @param defaults - The default values for all properties of type T
 * @returns A factory function that accepts optional overrides and returns a complete object
 *
 * @example
 * // Define a factory
 * const getDummyUser = createTestFactory<User>({
 *   id: 'user-1',
 *   name: 'Test User',
 *   email: 'test@example.com',
 * });
 *
 * // Use with defaults
 * const user = getDummyUser();
 *
 * // Override specific fields
 * const customUser = getDummyUser({ name: 'Custom Name' });
 *
 * // Set nullable fields to null
 * const nullableUser = getDummyUser({ email: null });
 *
 * @example
 * // Composing factories for nested objects
 * const getDummySettings = createTestFactory<Settings>({ theme: 'dark' });
 * const getDummyProfile = createTestFactory<Profile>({
 *   bio: 'Test bio',
 *   settings: getDummySettings(),
 * });
 *
 * // Override nested object by composing factories
 * const profile = getDummyProfile({
 *   settings: getDummySettings({ theme: 'light' }),
 * });
 */
export function createTestFactory<T extends object>(defaults: T) {
  return (overrides: RecursivePartial<T> = {} as RecursivePartial<T>): T => {
    return { ...defaults, ...overrides } as T;
  };
}
